// Symbol lookup is delegated to the shared currency table so admin + customer
// stay in sync and support the full popular + Caribbean currency set.
import { getCurrencySymbol as sharedGetCurrencySymbol } from '@shared/components';

/**
 * Formats a monetary amount with the appropriate currency symbol and formatting.
 *
 * @param amount - The amount to format
 * @param currency - The currency code ('USD', 'JMD', etc.)
 * @param includeSymbol - Whether to include the currency symbol. Defaults to true.
 * @returns A formatted currency string like "$25.00" or "25.00"
 */
export const formatCurrency = (
  amount: number | undefined | null,
  currency: string = 'USD',
  includeSymbol: boolean = true
): string => {
  if (amount === undefined || amount === null) {
    return 'N/A';
  }

  const formattedAmount = amount.toFixed(2);

  if (!includeSymbol) {
    return formattedAmount;
  }

  // Get currency symbol
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${formattedAmount}`;
};

/**
 * Gets the currency symbol for a given currency code.
 *
 * @param currency - The currency code ('USD', 'JMD', etc.)
 * @returns The currency symbol
 */
export const getCurrencySymbol = (currency: string): string =>
  sharedGetCurrencySymbol(currency);

/**
 * Formats a monetary amount with both USD and JMD equivalents.
 * Shows primary currency first, then the converted equivalent in parentheses.
 *
 * @param amount - The amount in the primary currency
 * @param useJmd - Whether the primary currency is JMD (true) or USD (false)
 * @param exchangeRate - The USD to JMD exchange rate
 * @returns A dual currency string like "$290.00 USD (J$46,980.00 JMD)"
 */
export const formatDualCurrency = (
  amount: number | undefined | null,
  useJmd: boolean,
  exchangeRate: number
): string => {
  if (amount === undefined || amount === null) {
    return 'N/A';
  }

  if (useJmd) {
    const jmdFormatted = `J$${amount.toFixed(2)} JMD`;
    const usdEquiv = exchangeRate > 0 ? amount / exchangeRate : 0;
    const usdFormatted = `$${usdEquiv.toFixed(2)} USD`;
    return `${jmdFormatted} (${usdFormatted})`;
  } else {
    const usdFormatted = `$${amount.toFixed(2)} USD`;
    const jmdEquiv = amount * exchangeRate;
    const jmdFormatted = `J$${jmdEquiv.toFixed(2)} JMD`;
    return `${usdFormatted} (${jmdFormatted})`;
  }
};

/**
 * Formats a currency amount for display in tables or compact spaces.
 *
 * @param amount - The amount to format
 * @param currency - The currency code ('USD', 'JMD', etc.)
 * @returns A compact formatted string
 */
export const formatCurrencyCompact = (
  amount: number | undefined | null,
  currency: string = 'USD'
): string => {
  if (amount === undefined || amount === null) {
    return '--';
  }

  const symbol = getCurrencySymbol(currency);
  
  if (amount >= 1000000) {
    return `${symbol}${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `${symbol}${(amount / 1000).toFixed(1)}K`;
  }

  return `${symbol}${amount.toFixed(2)}`;
};